import {
  type LineDatum,
  OddsTradingChartCanvas,
} from '@/components/opportunity/odds-trading-chart-canvas';
import { FreshnessOverlay, MoveChip } from '@/components/opportunity/odds-trading-chart-chips';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import type { MatchOddsHistoryOutput, OddsTickPoint } from '@tennis-scanner/shared';
import type { UTCTimestamp } from 'lightweight-charts';
import { Activity } from 'lucide-react';
import { useMemo, useState } from 'react';

// Trading-style chart for a single match. Reads dense per-tick history from
// TimescaleDB via analytics.matchOddsHistory, polls every 5s, and feeds the
// chart canvas (pure lightweight-charts) below.
//
// V2 features (this commit):
//   - Per-book detection — server picks Pinnacle preferred, falls back to
//     largest |move|. Old V1 mixed all books and produced false signals.
//   - Both directions — drops AND rises. The MoveChip colors them by
//     favorability (pick-drop and rival-rise = green; pick-rise and
//     rival-drop = red). Marker shape encodes direction (▼ down, ▲ up).
//   - Recovery suppression — moves that bounced back ≥50% from extreme
//     are filtered server-side; the chip won't show stale signals.
//   - Freshness indicator — overlay inside chart shows tick age with
//     traffic-light color so the operator sees data staleness without
//     leaving the cockpit.

const BEST_BOOK = '__best__'; // Sentinel for "show best across books".
const POLL_MS = 5_000;

interface ChartPick {
  selection: 'p1' | 'p2';
  targetOdds: number;
  /** Optional human names for the chart labels. */
  p1Name?: string | null | undefined;
  p2Name?: string | null | undefined;
}

interface Props {
  matchId: string;
  pick: ChartPick | undefined;
  /** Match scheduled start (ISO). Drives the match-start marker. */
  matchStartsAt?: string | null | undefined;
  sinceMinutes?: number;
}

export function OddsTradingChart({ matchId, pick, matchStartsAt, sinceMinutes = 240 }: Props) {
  const [bookFilter, setBookFilter] = useState<string>(BEST_BOOK);

  const query = trpc.analytics.matchOddsHistory.useQuery(
    { matchId, market: 'h2h', sinceMinutes },
    { refetchInterval: POLL_MS, staleTime: POLL_MS, enabled: matchId.length > 0 },
  );

  if (!query.data && query.isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="h-72 animate-pulse rounded bg-muted/30" />
      </div>
    );
  }

  if (!query.data || query.data.ticks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card py-10 text-center">
        <Activity className="size-6 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">
          Sin ticks en TimescaleDB para este partido
        </p>
        <p className="max-w-md text-xs text-muted-foreground">
          El connector publica aprox cada 10s. Para partidos recién creados o fuera del feed de
          api-tennis puede no haber data — esperá ~30s o revisá que el partido tenga cobertura.
        </p>
      </div>
    );
  }

  return (
    <ChartView
      data={query.data}
      pick={pick}
      bookFilter={bookFilter}
      onBookFilterChange={setBookFilter}
      matchStartsAt={matchStartsAt ?? undefined}
    />
  );
}

interface ChartViewProps {
  data: MatchOddsHistoryOutput;
  pick: ChartPick | undefined;
  bookFilter: string;
  onBookFilterChange: (v: string) => void;
  matchStartsAt: string | undefined;
}

function ChartView({ data, pick, bookFilter, onBookFilterChange, matchStartsAt }: ChartViewProps) {
  const series = useMemo(() => buildSeriesPair(data.ticks, bookFilter), [data.ticks, bookFilter]);
  // Latest tick observedAt across the full feed — drives the freshness
  // overlay. We use the data's last tick (server-truthful) rather than
  // the client clock or query.dataUpdatedAt (which only reflects when
  // the network call returned, not when the data was actually emitted).
  const latestObservedAt = data.ticks[data.ticks.length - 1]?.observedAt ?? null;

  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-3">
      <ChartHeader
        data={data}
        pick={pick}
        bookFilter={bookFilter}
        onBookFilterChange={onBookFilterChange}
      />
      <div className="relative">
        <FreshnessOverlay latestObservedAt={latestObservedAt} />
        <OddsTradingChartCanvas
          p1Data={series.p1}
          p2Data={series.p2}
          pickSide={pick?.selection}
          targetOdds={pick?.targetOdds}
          p1Label={pick?.p1Name ?? 'p1'}
          p2Label={pick?.p2Name ?? 'p2'}
          moveP1={data.moveP1}
          moveP2={data.moveP2}
          matchStartsAt={matchStartsAt}
        />
      </div>
    </div>
  );
}

interface ChartHeaderProps {
  data: MatchOddsHistoryOutput;
  pick: ChartPick | undefined;
  bookFilter: string;
  onBookFilterChange: (v: string) => void;
}

function ChartHeader({ data, pick, bookFilter, onBookFilterChange }: ChartHeaderProps) {
  const pickMove = pick?.selection === 'p2' ? data.moveP2 : data.moveP1;
  const rivalMove = pick?.selection === 'p2' ? data.moveP1 : data.moveP2;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
      <div className="flex items-baseline gap-3 font-mono uppercase tracking-wider text-muted-foreground">
        <span>movimiento · últimos {data.windowMinutes}m</span>
        <span>·</span>
        <span>
          {data.ticks.length} ticks · {data.books.length} casa
          {data.books.length === 1 ? '' : 's'}
        </span>
        {data.truncated && <span className="text-warning">(truncado)</span>}
      </div>
      <div className="flex items-center gap-2">
        {pick && pickMove && <MoveChip move={pickMove} side="pick" />}
        {pick && rivalMove && <MoveChip move={rivalMove} side="rival" />}
        <BookSelector books={data.books} value={bookFilter} onChange={onBookFilterChange} />
      </div>
    </div>
  );
}

function BookSelector({
  books,
  value,
  onChange,
}: {
  books: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  // Hide the selector when there's only one book — adds no value and
  // wastes operator's eye-time.
  if (books.length <= 1) return null;
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-7 w-[150px] font-mono text-[11px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={BEST_BOOK}>Mejor cuota</SelectItem>
        {books.map((b) => (
          <SelectItem key={b} value={b}>
            {b}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Build (p1, p2) line series. When bookFilter === BEST_BOOK we pick the
// highest-odds-across-books at each timestamp (the operator's "what could
// I have taken" line). Otherwise we filter ticks to the chosen book.
function buildSeriesPair(
  ticks: OddsTickPoint[],
  bookFilter: string,
): { p1: LineDatum[]; p2: LineDatum[] } {
  const filtered = bookFilter === BEST_BOOK ? ticks : ticks.filter((t) => t.book === bookFilter);
  const p1ByTime = new Map<number, number>();
  const p2ByTime = new Map<number, number>();
  for (const t of filtered) {
    const ts = Math.floor(Date.parse(t.observedAt) / 1000);
    if (!Number.isFinite(ts)) continue;
    const curP1 = p1ByTime.get(ts);
    if (curP1 === undefined || t.oddsP1 > curP1) p1ByTime.set(ts, t.oddsP1);
    const curP2 = p2ByTime.get(ts);
    if (curP2 === undefined || t.oddsP2 > curP2) p2ByTime.set(ts, t.oddsP2);
  }
  const toLine = (m: Map<number, number>): LineDatum[] =>
    Array.from(m.entries())
      .map(([time, value]) => ({ time: time as UTCTimestamp, value }))
      .sort((a, b) => a.time - b.time);
  return { p1: toLine(p1ByTime), p2: toLine(p2ByTime) };
}
